// 📍 src/pages/Payments.jsx
//
// Two data sources, in order of trust:
//
//   1. `adminListSubscriptions` Cloud Function — calls Stripe's API directly
//      (`/v1/subscriptions`) as the SOURCE OF TRUTH for who is currently
//      paying us. This is what we display in the "Active Subscriptions"
//      table. No dependency on webhooks firing or the client running
//      `sendReceiptEmail` — if Stripe says they have an active sub, they
//      show up here, period.
//
//   2. `receipts` Firestore collection — written by the Stripe webhook
//      (`invoice.paid`) and by client `sendReceiptEmail`. Used as the
//      "Recent Receipts" log because it includes one row per *payment
//      event*, not per active subscription. Useful for revenue-over-time
//      and for spotting payments that don't match an active sub (refunds,
//      stale rows, etc.).
//
// Stats cards aggregate from #1 (live Stripe state). The chart is also
// from #1. The receipts table is for forensic browsing.

import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, limit, getDocs,
  startAfter, where, Timestamp, getCountFromServer } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { PageHeader, Table, Badge, Card, StatCard,
  GoldBtn, TabButtons, Spinner } from '../components/UI';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const ACCENT = '#008751';
const GOLD = '#D4AF37';
const PAGE_SIZE = 30;

const adminListSubscriptionsFn = httpsCallable(functions, 'adminListSubscriptions');

const CHART_TOOLTIP = {
  contentStyle: { background: '#1A2332', border: '1px solid rgba(0,135,81,0.2)',
    borderRadius: 10, fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' },
  itemStyle: { color: '#fff' },
  labelStyle: { color: 'rgba(255,255,255,0.5)', marginBottom: 4 },
};

const TIER_COLORS = {
  free: '#6b7280', student: '#008751', premium: '#3b82f6',
  scholar: '#D4AF37', family: '#a855f7',
};

const TIER_LABELS = {
  student: 'Student', premium: 'Oga', scholar: 'Odogwu', family: 'Family', free: 'Free',
};

const STATUS_COLORS = {
  active: '#00b894', trialing: '#3b82f6', past_due: '#f97316',
  unpaid: '#ef4444', canceled: '#6b7280', incomplete: '#a855f7',
  incomplete_expired: '#6b7280',
};

// Format minor-units amount (cents) to a display string with the right symbol.
const fmtMoney = (amount, currency) => {
  const symbols = { usd: '$', gbp: '£', ngn: '₦', cad: 'C$', zar: 'R', ghs: 'GH₵', eur: '€', aud: 'A$' };
  const sym = symbols[(currency || 'usd').toLowerCase()] || '$';
  const amt = ((amount || 0) / 100).toFixed(2);
  return `${sym}${amt}`;
};

const fmtIso = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

const fmtDateOnly = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

// Days remaining as colored badge — red ≤3, orange ≤7, green otherwise.
const RenewalBadge = ({ iso }) => {
  if (!iso) return <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>;
  const d = new Date(iso);
  const diff = d.getTime() - Date.now();
  if (diff < 0) {
    return <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 12 }}>Expired</span>;
  }
  const days = Math.ceil(diff / 86400000);
  const color = days <= 3 ? '#ef4444' : days <= 7 ? '#f97316' : ACCENT;
  return <span style={{ color, fontWeight: 600, fontSize: 12 }}>{days}d</span>;
};

export default function Payments() {
  // ── Live subscriptions (primary table) ───────────────────────────────────
  const [subs, setSubs]                 = useState([]);
  const [subsLoading, setSubsLoading]   = useState(true);
  const [subsError, setSubsError]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // ── Receipts (secondary "log") ───────────────────────────────────────────
  const [receipts, setReceipts]   = useState([]);
  const [recLoading, setRecLoading] = useState(true);
  const [recLastDoc, setRecLastDoc] = useState(null);
  const [recHasMore, setRecHasMore] = useState(true);

  const loadSubs = useCallback(async () => {
    setSubsLoading(true);
    setSubsError('');
    try {
      const result = await adminListSubscriptionsFn({ status: statusFilter, limit: 100 });
      setSubs(result?.data?.subscriptions || []);
    } catch (e) {
      console.error('adminListSubscriptions failed:', e);
      setSubsError(e.code === 'functions/failed-precondition'
        ? 'Stripe key not configured on server.'
        : (e.message || 'Could not load Stripe subscriptions.'));
      setSubs([]);
    }
    setSubsLoading(false);
  }, [statusFilter]);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  // Fetch a page of receipts. `cursor` is the last doc from a previous page,
  // or null for the initial load. Pulling cursor in via parameter (rather
  // than via closure over `recLastDoc`) keeps this function dep-free, so the
  // useEffect that runs it on mount doesn't need an exhaustive-deps suppress.
  const loadReceiptsPage = useCallback(async (cursor) => {
    setRecLoading(true);
    try {
      let q = query(collection(db, 'receipts'),
        orderBy('paidAt', 'desc'), limit(PAGE_SIZE));
      if (cursor) q = query(q, startAfter(cursor));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReceipts((prev) => cursor ? [...prev, ...data] : data);
      setRecLastDoc(snap.docs[snap.docs.length - 1] || null);
      setRecHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error('receipts load failed:', e);
    }
    setRecLoading(false);
  }, []);

  useEffect(() => { loadReceiptsPage(null); }, [loadReceiptsPage]);

  // ── Aggregate stats from live Stripe data ────────────────────────────────
  const stats = (() => {
    const active = subs.filter((s) => s.status === 'active' || s.status === 'trialing');
    let mrr = 0;            // monthly-equivalent revenue, in minor units (cents)
    const byTier = {};
    active.forEach((s) => {
      mrr += (s.amount || 0);
      const tier = s.tier || 'unknown';
      byTier[tier] = (byTier[tier] || 0) + (s.amount || 0);
    });
    return {
      activeCount: active.length,
      totalCount: subs.length,
      mrr: (mrr / 100).toFixed(2),
      avg: active.length ? (mrr / active.length / 100).toFixed(2) : '0.00',
      tierTotals: byTier,
    };
  })();

  const tierRevenueArr = Object.entries(stats.tierTotals).map(([tier, amt]) => ({
    name: TIER_LABELS[tier] || tier,
    value: parseFloat((amt / 100).toFixed(2)),
    color: TIER_COLORS[tier] || '#6b7280',
  })).filter((t) => t.value > 0);

  // ── Subscription columns ─────────────────────────────────────────────────
  const SUB_COLUMNS = [
    {
      key: 'email', label: 'User',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(0,135,81,0.15), rgba(212,175,55,0.1))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: ACCENT, flexShrink: 0,
            border: '1px solid rgba(0,135,81,0.15)',
          }}>
            {(row.name || v || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#fff' }}>
              {row.name || '—'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>
              {v || '—'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'country', label: 'Region',
      render: (v) => v ? (
        <span style={{ fontFamily: 'DM Mono', fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
          {v}
        </span>
      ) : <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>,
    },
    {
      key: 'tier', label: 'Tier',
      render: (v) => v
        ? <Badge label={TIER_LABELS[v] || v} color={v} />
        : <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>,
    },
    {
      key: 'amount', label: 'Amount',
      render: (v, r) => (
        <span style={{ fontFamily: 'DM Mono', color: GOLD, fontWeight: 700, fontSize: 14 }}>
          {fmtMoney(v, r.currency)}
        </span>
      ),
    },
    {
      key: 'taxAmount', label: 'Tax',
      render: (v, r) => v
        ? <span style={{ fontFamily: 'DM Mono', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
            {fmtMoney(v, r.currency)}
          </span>
        : <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>—</span>,
    },
    {
      key: 'status', label: 'Status',
      render: (v, r) => (
        <span style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          background: `${STATUS_COLORS[v] || '#6b7280'}22`,
          color: STATUS_COLORS[v] || '#6b7280',
          border: `1px solid ${STATUS_COLORS[v] || '#6b7280'}55`,
        }}>
          {v || 'unknown'}
          {r.cancelAtPeriodEnd ? ' (ending)' : ''}
        </span>
      ),
    },
    {
      key: 'created', label: 'Started',
      render: fmtDateOnly,
    },
    {
      key: 'currentPeriodEnd', label: 'Renews',
      render: (v) => <RenewalBadge iso={v} />,
    },
    {
      key: 'lastInvoicePaidAt', label: 'Last Paid',
      render: fmtIso,
    },
  ];

  // ── Receipt columns (secondary table) ────────────────────────────────────
  const RECEIPT_COLUMNS = [
    {
      key: 'email', label: 'User',
      render: (v) => v
        ? <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>{v}</span>
        : <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>,
    },
    {
      key: 'tier', label: 'Tier',
      render: (v) => v
        ? <Badge label={TIER_LABELS[v] || v} color={v} />
        : <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>,
    },
    {
      key: 'amount', label: 'Amount',
      render: (v, r) => (
        <span style={{ fontFamily: 'DM Mono', color: GOLD, fontWeight: 600, fontSize: 13 }}>
          {fmtMoney(v, r.currency)}
        </span>
      ),
    },
    {
      key: 'paidAt', label: 'Paid',
      render: (v) => v?.toDate
        ? fmtIso(v.toDate().toISOString())
        : '—',
    },
    {
      key: 'id', label: 'Receipt ID',
      render: (v) => (
        <span style={{ fontFamily: 'DM Mono', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
          #{v.substring(0, 10)}
        </span>
      ),
    },
  ];

  const STATUS_TABS = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'trialing', label: 'Trialing' },
    { value: 'past_due', label: 'Past Due' },
    { value: 'canceled', label: 'Canceled' },
  ];

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Payments"
        subtitle="Live subscription state from Stripe + receipt history" />

      {/* ── Stats from live Stripe data ───────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
        gap: 16, marginBottom: 24,
      }}>
        <StatCard label="Active Subscribers" value={stats.activeCount}
          icon="🟢" color={ACCENT} borderColor={ACCENT}
          sub={subs.length ? `${subs.length} total in Stripe` : 'Loading…'} />
        <StatCard label="Monthly Revenue" value={`$${stats.mrr}`}
          icon="💰" color={GOLD} borderColor={GOLD}
          sub="Sum of active subs" />
        <StatCard label="Avg / Subscriber" value={`$${stats.avg}`}
          icon="📊" color="#3b82f6" borderColor="#3b82f6" />
        <StatCard label="Total Subs (any state)" value={stats.totalCount}
          icon="📋" color="#a855f7" borderColor="#a855f7"
          sub="Includes canceled / past_due" />
      </div>

      {/* ── Tier breakdown chart ──────────────────────────────────────────── */}
      {tierRevenueArr.length > 0 && (
        <div style={{
          background: '#1A2332',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 16, padding: 24, marginBottom: 24,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 16 }}>
            Active Revenue by Tier
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie data={tierRevenueArr} innerRadius={56} outerRadius={84}
                  paddingAngle={3} dataKey="value" strokeWidth={0}>
                  {tierRevenueArr.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip {...CHART_TOOLTIP}
                  formatter={(v) => [`$${v}`, 'Revenue']} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tierRevenueArr.map((d, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', background: 'rgba(255,255,255,0.02)', borderRadius: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3,
                      background: d.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{d.name}</span>
                  </div>
                  <span style={{ fontFamily: 'DM Mono', fontSize: 14, color: GOLD, fontWeight: 700 }}>
                    ${d.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Live Stripe Subscriptions table ───────────────────────────────── */}
      <div style={{
        fontSize: 14, fontWeight: 700, color: '#fff',
        marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        🔴 Live from Stripe — Active Subscriptions
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>
          (source of truth; pulled directly via Stripe API)
        </span>
      </div>
      <TabButtons tabs={STATUS_TABS} active={statusFilter} onChange={setStatusFilter} />
      {subsError && (
        <div style={{
          padding: '12px 14px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 10, color: '#ef4444', fontSize: 13, marginBottom: 12,
        }}>
          ⚠️ {subsError}
        </div>
      )}
      <div style={{
        background: '#1A2332', border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 16, overflow: 'hidden', marginBottom: 32,
      }}>
        <Table columns={SUB_COLUMNS} data={subs}
          loading={subsLoading && subs.length === 0} />
      </div>

      {/* ── Receipts log (secondary) ──────────────────────────────────────── */}
      <div style={{
        fontSize: 14, fontWeight: 700, color: '#fff',
        marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        🧾 Receipts Log
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>
          (one row per payment event recorded by the webhook)
        </span>
      </div>
      <div style={{
        background: '#1A2332', border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 16, overflow: 'hidden', marginBottom: 16,
      }}>
        <Table columns={RECEIPT_COLUMNS} data={receipts}
          loading={recLoading && receipts.length === 0} />
      </div>
      {recHasMore && !recLoading && (
        <div style={{ textAlign: 'center' }}>
          <GoldBtn onClick={() => loadReceiptsPage(recLastDoc)}>Load More Receipts</GoldBtn>
        </div>
      )}
      {recLoading && receipts.length > 0 && <Spinner />}
    </div>
  );
}
